// @ts-check
import { ChevronDown, ChevronUp, Copy, Edit2, Plus, RefreshCw, Ruler, Save, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import FileUpload from '../components/FileUpload';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import api from '../utils/apiClient';

const SizeGuidesPage = () => {
  const toast = useToast();
  const [guides, setGuides] = useState([]);
  const [sizeVariants, setSizeVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [expandedGuide, setExpandedGuide] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    country: '',
    country_code: '',
    title: '',
    description: '',
    default_unit: 'cm',
    fit_guide: '',
    care_instructions: '',
    body_measurement_image: '',
    is_active: true,
    variant_ids: [],
  };

  const [form, setForm] = useState({ ...emptyForm });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [guidesData, variantsData] = await Promise.all([
        api.get('/api/admin/size-guides'),
        api.get('/api/admin/catalog/size-variants'),
      ]);
      setGuides(guidesData || []);
      setSizeVariants(variantsData || []);
    } catch (err) {
      toast.error('Failed to load size guides');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!form.country || !form.country_code || !form.title) {
      toast.error('Country, country code, and title are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/admin/size-guides', form);
      toast.success('Size guide created');
      setShowCreateForm(false);
      setForm({ ...emptyForm });
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to create size guide');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingGuide) return;
    setSaving(true);
    try {
      await api.patch(`/api/admin/size-guides/${editingGuide}`, form);
      toast.success('Size guide updated');
      setEditingGuide(null);
      setForm({ ...emptyForm });
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to update size guide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (guideId) => {
    if (!window.confirm('Are you sure you want to delete this size guide?')) return;
    try {
      await api.del(`/api/admin/size-guides/${guideId}`);
      toast.success('Size guide deleted');
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete size guide');
    }
  };

  const startEdit = (guide) => {
    setEditingGuide(guide.id);
    setShowCreateForm(false);
    setForm({
      country: guide.country,
      country_code: guide.country_code,
      title: guide.title,
      description: guide.description || '',
      default_unit: guide.default_unit || 'cm',
      fit_guide: guide.fit_guide || '',
      care_instructions: guide.care_instructions || '',
      is_active: guide.is_active,
      body_measurement_image: guide.body_measurement_image || '',
      variant_ids: (guide.size_variants || []).map((sv) => sv.size_variant_id),
    });
  };

  // Most guides across countries share most of their fit-guide/care-instruction copy and
  // linked variants — start a new one from an existing guide instead of a blank form.
  const startDuplicate = (guide) => {
    setEditingGuide(null);
    setShowCreateForm(true);
    setForm({
      country: '',
      country_code: '',
      title: '',
      description: guide.description || '',
      default_unit: guide.default_unit || 'cm',
      fit_guide: guide.fit_guide || '',
      care_instructions: guide.care_instructions || '',
      is_active: guide.is_active,
      body_measurement_image: guide.body_measurement_image || '',
      variant_ids: (guide.size_variants || []).map((sv) => sv.size_variant_id),
    });
  };

  const cancelEdit = () => {
    setEditingGuide(null);
    setShowCreateForm(false);
    setForm({ ...emptyForm });
  };

  const toggleVariant = (variantId) => {
    setForm((prev) => {
      const ids = prev.variant_ids.includes(variantId)
        ? prev.variant_ids.filter((id) => id !== variantId)
        : [...prev.variant_ids, variantId];
      return { ...prev, variant_ids: ids };
    });
  };

  if (loading) {
    return (
      <div>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Ruler size={24} />
          <h1 className="text-3xl font-serif">Size Guides</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="p-2 border border-input rounded-lg hover:bg-muted/50 transition"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={() => {
              setShowCreateForm(true);
              setEditingGuide(null);
              setForm({ ...emptyForm });
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition flex items-center gap-2 text-sm"
          >
            <Plus size={18} />
            Add Size Guide
          </button>
        </div>
      </div>

      {/* Create / Edit Form */}
      {(showCreateForm || editingGuide) && (
        <div className="mb-6 bg-card border border-border rounded-lg p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-serif">{editingGuide ? 'Edit Size Guide' : 'New Size Guide'}</h2>
            <button onClick={cancelEdit} className="p-1 hover:bg-accent rounded-lg">
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Country *</label>
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
                  placeholder="e.g. India"
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Country Code *</label>
                <input
                  type="text"
                  value={form.country_code}
                  onChange={(e) => setForm((prev) => ({ ...prev, country_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. IN"
                  maxLength={10}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Default Unit</label>
                <select
                  value={form.default_unit}
                  onChange={(e) => setForm((prev) => ({ ...prev, default_unit: e.target.value }))}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
                >
                  <option value="cm">Centimeters (cm)</option>
                  <option value="inches">Inches</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. India Size Guide"
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
                placeholder="Optional description..."
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Fit Guide</label>
              <textarea
                value={form.fit_guide}
                onChange={(e) => setForm((prev) => ({ ...prev, fit_guide: e.target.value }))}
                rows={2}
                placeholder="Fit recommendations..."
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Care Instructions</label>
              <textarea
                value={form.care_instructions}
                onChange={(e) => setForm((prev) => ({ ...prev, care_instructions: e.target.value }))}
                rows={2}
                placeholder="Care instructions..."
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground text-sm"
              />
            </div>

            {/* Body Measurement Diagram */}
            <div>
              <label className="block text-sm font-medium mb-2">Body Measurement Diagram</label>
              {form.body_measurement_image ? (
                <div className="relative inline-block">
                  <img
                    src={form.body_measurement_image}
                    alt="Body measurement diagram"
                    className="max-w-[200px] rounded-lg border border-border"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, body_measurement_image: '' }))}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <FileUpload
                  uploadType="about-media"
                  multiple={false}
                  maxFiles={1}
                  accept="image/*"
                  onUploadComplete={(files) => {
                    if (files && files.length > 0) {
                      setForm((prev) => ({ ...prev, body_measurement_image: files[0].url }));
                    }
                  }}
                />
              )}
              <p className="text-xs text-muted-foreground/70 mt-1">
                Upload a diagram showing where to take body measurements.
              </p>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium">Active</label>
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, is_active: !prev.is_active }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.is_active ? 'bg-primary' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                    form.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Size Variants Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Linked Size Variants ({form.variant_ids.length} selected)
              </label>
              {sizeVariants.length === 0 ? (
                <p className="text-sm text-muted-foreground/70">
                  No size variants available. Create them in Catalog Settings.
                </p>
              ) : (
                <div className="border border-border rounded-lg p-3 max-h-60 overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
                    {sizeVariants.map((variant) => {
                      const isSelected = form.variant_ids.includes(variant.id);
                      return (
                        <label
                          key={variant.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer transition ${
                            isSelected
                              ? 'bg-black/5 text-foreground font-medium'
                              : 'text-muted-foreground hover:bg-muted/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleVariant(variant.id)}
                            className="rounded-lg border-input"
                          />
                          <span className="truncate">
                            {variant.primary_name}
                            {variant.secondary_name ? ` (${variant.secondary_name})` : ''}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground/70 mt-1">The display order matches the selection order.</p>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={editingGuide ? handleUpdate : handleCreate}
                disabled={saving}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition flex items-center gap-2 text-sm disabled:opacity-50"
              >
                <Save size={16} />
                {saving ? 'Saving...' : editingGuide ? 'Update' : 'Create'}
              </button>
              <button
                onClick={cancelEdit}
                className="px-4 py-2 border border-input rounded-lg hover:bg-muted/50 transition text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guides List */}
      {guides.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          <Ruler size={32} className="mx-auto mb-3 text-muted-foreground/50" />
          <p>No size guides yet. Click "Add Size Guide" to create one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {guides.map((guide) => (
            <div key={guide.id} className="bg-card border border-border rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition"
                onClick={() => setExpandedGuide(expandedGuide === guide.id ? null : guide.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg font-medium">{guide.country}</span>
                  <span className="text-xs bg-muted px-2 py-0.5 rounded-md font-mono">{guide.country_code}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      guide.is_active
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {guide.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground/70">{guide.default_unit}</span>
                  {expandedGuide === guide.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </div>

              {expandedGuide === guide.id && (
                <div className="border-t border-border p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-muted-foreground">Title:</span>
                      <span className="ml-2">{guide.title}</span>
                    </div>
                    {guide.description && (
                      <div>
                        <span className="text-muted-foreground">Description:</span>
                        <span className="ml-2">{guide.description}</span>
                      </div>
                    )}
                    {guide.fit_guide && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Fit Guide:</span>
                        <p className="mt-1 text-sm text-foreground/80">{guide.fit_guide}</p>
                      </div>
                    )}
                    {guide.care_instructions && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Care Instructions:</span>
                        <p className="mt-1 text-sm text-foreground/80">{guide.care_instructions}</p>
                      </div>
                    )}
                  </div>

                  {guide.size_variants && guide.size_variants.length > 0 && (
                    <div className="mb-4">
                      <span className="text-sm text-muted-foreground block mb-2">
                        Linked Variants ({guide.size_variants.length}):
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {guide.size_variants
                          .sort((a, b) => a.display_order - b.display_order)
                          .map((sv) => {
                            const variant = sizeVariants.find((v) => v.id === sv.size_variant_id);
                            return (
                              <span key={sv.id} className="text-xs bg-muted px-2 py-1 rounded-md">
                                {variant?.primary_name || sv.size_variant_id}
                              </span>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(guide);
                      }}
                      className="px-3 py-1.5 text-xs border border-input rounded-md hover:bg-accent transition flex items-center gap-1"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startDuplicate(guide);
                      }}
                      className="px-3 py-1.5 text-xs border border-input rounded-md hover:bg-accent transition flex items-center gap-1"
                      title="Start a new guide pre-filled from this one"
                    >
                      <Copy size={14} />
                      Duplicate
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(guide.id);
                      }}
                      className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 rounded-md hover:bg-red-50 dark:hover:bg-red-950/70 transition flex items-center gap-1"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SizeGuidesPage;
